'use client';

import {Globe} from 'lucide-react';
import React, {useState} from 'react';
import {usePathname, useRouter} from '#i18n/navigation.ts';
import {useParams, useSearchParams} from 'next/navigation';
import LanguagePickerPopupMenu from './LanguagePickerPopupMenu';
import {Button} from '@purpose/ui/components/button';
import {cn} from '@purpose/ui/lib/utils';
import {useUnsavedChanges} from '@/src/contexts/UnsavedChangesContext';
import UnsavedChangesModal from '../UnsavedChangesModal';

function LanguagePicker({
  inversion = false,
  rightMenu = false,
  bottomMenu = false,
  buttonClassName,
  iconClassName
}: {
  inversion?: boolean;
  rightMenu?: boolean;
  bottomMenu?: boolean;
  buttonClassName?: string;
  iconClassName?: string;
}) {
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const {locale} = useParams<{locale: string}>();
  const router = useRouter();
  const pathname = usePathname();

  const [anchorEl, setAnchorEl] = useState(null);
  const [newLocale, setNewLocale] = useState<string | null>(null);

  const {link, setLink, open, setOpen} = useUnsavedChanges();

  function handleClose() {
    setAnchorEl(null);
  }

  function changeLanguage(lngCode: string) {
    handleClose();
    if (newLocale || lngCode) {
      router.push(
        {
          pathname,
          query
        },
        {locale: newLocale ?? lngCode}
      );
    }
    setNewLocale(null);
    setLink(undefined);
  }

  function handleClick(event) {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  }

  function handleChangeLocale(lngCode: string) {
    if (link) {
      handleClose();
      setNewLocale(lngCode);
      setOpen(true);
    } else {
      changeLanguage(lngCode);
    }
  }

  return (
    <div>
      <UnsavedChangesModal
        link={link}
        setLink={setLink}
        open={open}
        setOpen={setOpen}
        onSave={changeLanguage as () => void}
      />
      <Button
        variant="ghost"
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        className={cn(
          `${inversion && 'hover:bg-black/20 active:bg-black/20'} cursor-pointer touch-manipulation`,
          buttonClassName
        )}
        aria-label="Endre språk"
      >
        <Globe
          className={cn(
            inversion ? 'text-white' : 'text-primary-80',
            iconClassName
          )}
        />
      </Button>

      <LanguagePickerPopupMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        rightMenu={rightMenu}
        bottomMenu={bottomMenu}
        locale={locale}
        onChange={handleChangeLocale}
      />
    </div>
  );
}

export default LanguagePicker;
